import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Menu } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';
import pick from 'lodash/pick';

import { useSteps } from '../../hooks';
import DeleteStep from '../DeleteStep';
import ArchiveStep from '../ArchiveStep';
import ConfirmStep from '../ConfirmStep';
import ListMoveStep from '../ListMoveStep';

import styles from './ActionsStep.module.scss';

const StepTypes = {
  DELETE: 'DELETE',
  ARCHIVE: 'ARCHIVE',
  MOVE: 'MOVE',
  MOVE_CONFIRM: 'MOVE_CONFIRM',
};

const ActionsStep = React.memo(
  ({
    list,
    projectsToLists,
    onNameEdit,
    onCardAdd,
    onMove,
    onTransfer,
    onDelete,
    onArchive,
    onBoardFetch,
    canEdit,
    isSuperuser,
    isMonthlyReportAvailable,
    onMonthlyReportOpen,
    onClose,
  }) => {
  const [t] = useTranslation();
  const [step, openStep, handleBack] = useSteps();

  const handleEditNameClick = useCallback(() => {
    onNameEdit();
    onClose();
  }, [onNameEdit, onClose]);

  const handleAddCardClick = useCallback(() => {
    onCardAdd();
    onClose();
  }, [onCardAdd, onClose]);

  const handleDeleteClick = useCallback(() => {
    openStep(StepTypes.DELETE);
  }, [openStep]);

  const handleArchiveClick = useCallback(() => {
    openStep(StepTypes.ARCHIVE);
  }, [openStep]);

  const handleMoveClick = useCallback(() => {
    openStep(StepTypes.MOVE);
  }, [openStep]);

  const handleMoveConfirm = useCallback(() => {
    if (step && step.params) {
      const { boardId } = step.params;
      if (boardId !== list.boardId) {
        onTransfer(boardId);
      }
      onClose();
    }
  }, [step, list, onTransfer, onClose]);

  const handleMonthlyReportClick = useCallback(() => {
    onMonthlyReportOpen();
    onClose();
  }, [onMonthlyReportOpen, onClose]);

  if (step && step.type === StepTypes.DELETE) {
    return (
      <DeleteStep
        title="common.deleteList"
        content="common.areYouSureYouWantToDeleteThisList"
        buttonContent="action.deleteList"
        onConfirm={onDelete}
        onBack={handleBack}
      />
    );
  }

  if (step && step.type === StepTypes.ARCHIVE) {
    return (
      <ArchiveStep
        title="Архивировать"
        content="Поместить список в архив?"
        buttonContent="Архивировать"
        onConfirm={onArchive}
        onBack={handleBack}
      />
    );
  }

  if (step && step.type === StepTypes.MOVE) {
    return (
      <ListMoveStep
        projectsToLists={projectsToLists}
        defaultPath={pick(list, ['projectId', 'boardId'])}
        onMove={onMove}
        onTransfer={(boardId) => openStep(StepTypes.MOVE_CONFIRM, { boardId })}
        onBoardFetch={onBoardFetch}
        onBack={handleBack}
        onClose={onClose}
      />
    );
  }

  if (step && step.type === StepTypes.MOVE_CONFIRM) {
    return (
      <ConfirmStep
        title="common.moveList"
        content="common.areYouSureYouWantToMoveThisList"
        buttonContent="action.move"
        onConfirm={handleMoveConfirm}
        onBack={handleBack}
      />
    );
  }

  return (
    <>
      <Popup.Header>
        {t('common.listActions', {
          context: 'title',
        })}
      </Popup.Header>
      <Popup.Content>
        <Menu secondary vertical className={styles.menu}>
          {canEdit && (
            <Menu.Item className={styles.menuItem} onClick={handleEditNameClick}>
              {t('action.editTitle', {
                context: 'title',
              })}
            </Menu.Item>
          )}
          {canEdit && (
            <Menu.Item className={styles.menuItem} onClick={handleAddCardClick}>
              {t('action.addCard', {
                context: 'title',
              })}
            </Menu.Item>
          )}

          {isMonthlyReportAvailable && (
            <Menu.Item className={styles.menuItem} onClick={handleMonthlyReportClick}>
              {t('Отчет за месяц')}
            </Menu.Item>
          )}

          {canEdit && (
            <Menu.Item className={styles.menuItem} onClick={handleMoveClick}>
              {t('action.move')}
            </Menu.Item>
          )}

          {canEdit && (
            <Menu.Item className={styles.menuItem} onClick={handleArchiveClick}>
              {t('В архив', {
                context: 'title',
              })}
            </Menu.Item>
          )}
        </Menu>
      </Popup.Content>
    </>
  );
});

ActionsStep.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  list: PropTypes.object.isRequired,
  projectsToLists: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  onNameEdit: PropTypes.func.isRequired,
  onCardAdd: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  canEdit: PropTypes.bool.isRequired,
  isSuperuser: PropTypes.bool,
  isMonthlyReportAvailable: PropTypes.bool.isRequired,
  onMonthlyReportOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ActionsStep;
